import { ref, reactive, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';

/**
 * Reusable "order item actions" (cancel item / add item) shared across screens.
 *
 * @param {Object}   opts
 * @param {Function} opts.getOrderId - returns the current order id (used for orders.items.store)
 * @param {Function} opts.onSuccess  - called after a successful cancel or add (caller decides how to refresh)
 */
export function useOrderItemActions({ getOrderId, onSuccess } = {}) {
    const { proxy } = getCurrentInstance();
    const $t = proxy.$t;
    const toast = useToast();

    // --- Cancel single item ---------------------------------------------------
    const cancelItemState = ref({ visible: false, item: null, reason: '', processing: false });

    const openCancelItem = (item) => {
        cancelItemState.value = { visible: true, item, reason: '', processing: false };
    };

    const confirmCancelItem = async () => {
        const it = cancelItemState.value.item;
        if (!it) return;
        cancelItemState.value.processing = true;
        try {
            const url = proxy.route ? proxy.route('order-items.cancel', it.id) : `/admin/order-items/${it.id}/cancel`;
            await axios.put(url, { cancellation_reason: cancelItemState.value.reason.trim() || null });
            cancelItemState.value.visible = false;
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Item canceled successfully'), life: 3000 });
            onSuccess?.();
        } catch (e) {
            const msg = e?.response?.data?.message || $t('Failed to cancel item');
            toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 4000 });
        } finally {
            cancelItemState.value.processing = false;
        }
    };

    // --- Cancel multiple items (shared reason, ONE consolidated email) --------
    const bulkCancelState = ref({ visible: false, ids: [], reason: '', processing: false });

    const openBulkCancel = (ids) => {
        bulkCancelState.value = { visible: true, ids: [...(ids || [])], reason: '', processing: false };
    };

    const confirmBulkCancel = async () => {
        const ids = bulkCancelState.value.ids || [];
        if (!ids.length) return;
        const orderId = getOrderId?.();
        bulkCancelState.value.processing = true;
        try {
            const url = proxy.route ? proxy.route('orders.items.cancelBulk', orderId) : `/admin/orders/${orderId}/items/cancel-bulk`;
            await axios.post(url, { item_ids: ids, cancellation_reason: bulkCancelState.value.reason.trim() || null });
            bulkCancelState.value.visible = false;
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Items canceled successfully'), life: 3000 });
            onSuccess?.();
        } catch (e) {
            const msg = e?.response?.data?.message || $t('Failed to cancel the items. Please try again.');
            toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 4000 });
        } finally {
            bulkCancelState.value.processing = false;
        }
    };

    // --- Add item -------------------------------------------------------------
    const addItemState = ref({ visible: false, site: null, type_id: null, observations: '', processing: false });
    const siteSuggestions = ref([]);
    const siteOffers = ref([]);

    const openAddItem = () => {
        addItemState.value = { visible: true, site: null, type_id: null, observations: '', processing: false };
        siteSuggestions.value = [];
        siteOffers.value = [];
    };

    // When a site is selected, load its offers so a niche/type (with correct pricing) can be picked.
    const onSiteSelected = async (site) => {
        siteOffers.value = [];
        addItemState.value.type_id = null;
        const id = site?.id ?? site?.value?.id;
        if (!id) return;
        try {
            const url = proxy.route ? proxy.route('sites.details', id) : `/admin/sites/${id}/details`;
            const { data } = await axios.get(url);
            siteOffers.value = (data?.site?.offers || []).filter((o) => o.type_id);
            if (siteOffers.value.length === 1) {
                addItemState.value.type_id = siteOffers.value[0].type_id;
            }
        } catch (e) {
            siteOffers.value = [];
        }
    };

    // Human-readable label for a type option: "Type name — 12.34 USD".
    const offerLabel = (o) => {
        const name = o.type_name || $t('Type');
        if (o.sale == null || !o.sale_coin) return name;
        return `${name} — ${(o.sale / 100).toFixed(2)} ${o.sale_coin}`;
    };

    // Reuses the existing sites.checkDuplicate JSON endpoint to search sites by URL/domain.
    const searchSites = async (event) => {
        const q = (event.query || '').trim();
        if (!q) {
            siteSuggestions.value = [];
            return;
        }
        try {
            const url = proxy.route ? proxy.route('sites.checkDuplicate') : '/admin/sites/check-duplicate';
            const res = await axios.get(url, { params: { url: q } });
            const list = [];
            if (res.data?.exact_match) list.push(res.data.exact_match);
            if (Array.isArray(res.data?.similar_sites)) list.push(...res.data.similar_sites);
            const seen = new Set();
            siteSuggestions.value = list.filter((s) => s && !seen.has(s.id) && seen.add(s.id));
        } catch (e) {
            siteSuggestions.value = [];
        }
    };

    const submitAddItem = async () => {
        const siteId = addItemState.value.site?.id ?? addItemState.value.site;
        if (!siteId) {
            toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please select a site'), life: 3000 });
            return;
        }
        const orderId = getOrderId?.();
        addItemState.value.processing = true;
        try {
            const url = proxy.route ? proxy.route('orders.items.store', orderId) : `/admin/orders/${orderId}/items`;
            await axios.post(url, {
                site_id: siteId,
                type_id: addItemState.value.type_id ?? null,
                observations: addItemState.value.observations || null,
            });
            addItemState.value.visible = false;
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Item added successfully'), life: 3000 });
            onSuccess?.();
        } catch (e) {
            const msg = e?.response?.data?.message || $t('Failed to add item');
            toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 4000 });
        } finally {
            addItemState.value.processing = false;
        }
    };

    // NOTE: return a `reactive` object (not a plain object of refs). This composable
    // is consumed via `v-bind="itemActions"` onto <OrderItemActionsDialogs>. Vue does
    // NOT unwrap refs that are properties of a plain object, so a plain return would
    // pass Ref instances as props and `v-model:visible="addItemState.visible"` would
    // read `.visible` off a Ref (always undefined) — the dialogs would never open.
    // reactive() unwraps the refs on access, so the child receives plain values.
    return reactive({
        // state
        cancelItemState,
        bulkCancelState,
        addItemState,
        siteSuggestions,
        siteOffers,
        // handlers
        openCancelItem,
        confirmCancelItem,
        openBulkCancel,
        confirmBulkCancel,
        openAddItem,
        onSiteSelected,
        offerLabel,
        searchSites,
        submitAddItem,
    });
}
